import React, { useEffect, useRef, useState } from 'react';

import Appearance from 'styles/Appearance.js';
import { FloatingMenuIndex } from 'structure/Layer.js';
import PageControl from 'views/PageControl.js';
import Request from 'files/Request.js';
import TextField from 'views/TextField.js';
import Views, { AltBadge } from 'views/Main.js';

const LeadLookupField = ({ appendContent, containerStyle, icon, onChange, placeholder, removable = false, utils, value }) => {

    const limit = 5;
    const offset = useRef(0);

    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(false);
    const [text, setText] = useState(null);

    const onClearSelection = () => {

        // clear text state
        setText('');
        setSelected(null);

        // notify subscriber of data change
        if(typeof(onChange) === 'function') {
            onChange(null);
        }
    }

    const onClick = async lead => {

        // empty results array, set selected lead, and update text field value
        setResults([]);
        setSelected(lead);
        setText(lead.full_name);

        // notify subscribers of new selection
        if(typeof(onChange) === 'function') {
            onChange(lead);
        }
    }

    const onTextChange = text => {
        offset.current = 0;
        setText(text);
        fetchResults(text);
    }

    const fetchResults = async text => {
        try {

            // prevent moving forward if an invalid amount of text was provided
            if(!text || text.length < 3) {

                // end loading and empty results array
                setLoading(false);                    
                setResults([]);

                // notify subscribers that the selected lead has be deselected
                if(typeof(onChange) === 'function') {
                    onChange(null);
                }
                return;
            }

            // submit request to server
            setLoading(true);
            let { leads, paging } = await Request.post(utils, '/leads/', {
                filter_props: {},
                limit: limit,
                offset: offset.current,
                search_props: { text: text },
                sort_key: 'full_name',
                sort_type: 2,
                type: 'advanced_search'
            });
            console.log(leads);

            // end loading and set results object
            setLoading(false);
            setPaging(paging);
            setResults(leads);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue completing your search. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        if(value) {
            setSelected(value);
            setText(value.full_name);
        }
    }, [value]);

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            ...containerStyle
        }}>
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                minWidth: 0,
                width: '100%'
            }}>
                <TextField
                appendContent={appendContent}
                autoCapitalize={'sentences'}
                autoComplete={false}
                autoCorrect={false}
                icon={icon}
                loading={loading}
                onChange={onTextChange}
                placeholder={placeholder || 'Search by first and last name...'}
                useDelay={true}
                value={text}
                style={{
                    flexGrow: 1,
                    ...Appearance.textStyles.title()
                }}/>
                {selected?.id > 0 && removable === true && (
                    <img
                    className={'text-button'}
                    onClick={onClearSelection}
                    src={'images/delete-red-small.png'}
                    style={{
                        height: 25,
                        marginLeft: 8,
                        width: 25
                    }} />
                )}
            </div>
            {results.length > 0 && (
                <div style={{
                    backgroundColor: Appearance.colors.textField(),
                    borderColor: Appearance.colors.divider(),
                    borderRadius: 12,
                    borderStyle: 'solid',
                    borderWidth: 1,
                    marginTop: 8,
                    overflow: 'hidden',
                    padding: 0,
                    textAlign: 'left'
                }}>
                    {results.map((lead, index) => {
                        return (
                            Views.entry({
                                bottomBorder: index !== results.length - 1,
                                hideIcon: true,
                                key: index,
                                onClick: onClick.bind(this, lead),
                                title: lead.full_name,
                                style: { padding: '6px 12px 6px 12px' },
                                subTitle: lead.phone_number || 'No phone number provided',
                                rightContent: (
                                    <AltBadge 
                                    content={lead.status} 
                                    style={{
                                        marginRight: 0 
                                    }}/>
                                )
                            })
                        )
                    })}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        offset={offset.current}
                        onClick={props => {
                            offset.current = props;
                            fetchResults(text);
                        }} />
                    )}
                </div>
            )}
            {!selected && typeof(text) === 'string' && text.length > 0 && results.length === 0 && loading === false && (
                <div style={{
                    backgroundColor: Appearance.colors.textField(),
                    borderColor: Appearance.colors.divider(),
                    borderRadius: 12,
                    borderStyle: 'solid',
                    borderWidth: 1,
                    marginTop: 8,
                    overflow: 'hidden',
                    padding: 0,
                    textAlign: 'left'
                }}>
                    {Views.entry({
                        bottomBorder: false,
                        hideIcon: true,
                        subTitle: 'Nothing was found for your search',
                        title: 'No Leads Found',
                        onClick: setText.bind(this, null)
                    })}
                </div>
            )}
        </div>
    )
}
export default LeadLookupField;
